import clsx from "clsx";
import { AgingReport, AgingReportMessage, AgingReportMessageResponse, toMonthYear, toPrettyDate } from "common";
import React, { useState } from "react";
import { notify } from "../util/notify";
import { useUser } from "../store";
import { useConfirmation } from "./ConfirmationProvider";

export default function ReportMessage({ message, refreshReport, parentReport, onReply }: { parentReport: AgingReport, message: AgingReportMessage; onReply: (months: string[]) => void; refreshReport: (report: AgingReport) => void }) {
    const [collapsed, setCollapsed] = useState<boolean>(false);
    const { invokeApi, isAdmin, user } = useUser();
    const confirm = useConfirmation();


    const deleteReportMessage = async () => {
        const confirmed = await confirm({
            title: "Delete message?",
            message: "This message and its conversation will be permanently deleted.",
            confirmLabel: "Delete message",
        });
        if (!confirmed) return;
        invokeApi({
            endpoint: "/deleteReportMessage",
            data: {
                residentName: parentReport.residentName,
                billType: parentReport.billType,
                index: parentReport.index,
                messageId: message.id,
            },
        }).then((report: AgingReport) => {
            notify.success("Message deleted!");
            refreshReport(report);
        });
    };

    const deleteResponse = async (response: AgingReportMessageResponse) => {
        const confirmed = await confirm({
            title: "Delete response?",
            message: "This response will be permanently deleted.",
            confirmLabel: "Delete response",
        });
        if (!confirmed) return;
        invokeApi({
            endpoint: "/deleteReportMessageResponse",
            data: {
                residentName: parentReport.residentName,
                billType: parentReport.billType,
                index: parentReport.index,
                messageId: message.id,
                responseId: response.id,
            },
        }).then((report: AgingReport) => {
            notify.success("Response deleted!");
            refreshReport(report);
        });
    };

    const canDeleteMessage = (username: string) => isAdmin || username === user?.username;

    return (
        <div key={message.id} className="thread">
            <div className="thread-heading">
                <div className="month-badge">
                    <i className="fa-regular fa-calendar" aria-hidden="true" />
                    <span>{message.months.map(toMonthYear).join(' · ') || "N/A"}</span>
                </div>
                <span className="response-count">
                    <i className="fa-regular fa-comments" aria-hidden="true" />
                    {message.responses.length + 1} {message.responses.length === 0 ? 'message' : 'messages'}
                </span>
            </div>

            <div className="message-card">
                <div className="message-top">
                    <div className="message-text">
                        <strong className="message-author">{message.author}</strong>
                        <span>{message.message}</span>
                    </div>
                    <div className="message-right">
                        <button
                            className="message-action"
                            type="button"
                            title="Reply to message"
                            aria-label="Reply to message"
                            onClick={() => onReply(message.months)}
                        >
                            <i className="fa-solid fa-reply" aria-hidden="true" />
                        </button>
                        {canDeleteMessage(message.author) &&
                            <button
                                className="message-action message-action-delete"
                                type="button"
                                title="Delete message"
                                aria-label="Delete message"
                                onClick={deleteReportMessage}
                            >
                                <i className="fa-solid fa-trash" aria-hidden="true" />
                            </button>
                        }
                        <button
                            className="message-action"
                            type="button"
                            title={collapsed ? "Expand conversation" : "Collapse conversation"}
                            aria-label={collapsed ? "Expand conversation" : "Collapse conversation"}
                            aria-expanded={!collapsed}
                            onClick={() => setCollapsed(!collapsed)}
                        >
                            <i className={`fa-solid ${collapsed ? 'fa-chevron-down' : 'fa-chevron-up'}`} aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <div className="meta">
                    <span><i className="fa-regular fa-clock" aria-hidden="true" /> {toPrettyDate(message.createdAt)}</span>
                    {message.deadline && <span className="deadline"><i className="fa-regular fa-calendar-check" aria-hidden="true" /> Due {toPrettyDate(message.deadline)}</span>}
                </div>
            </div>

            {!collapsed && (
                <div className="responses">
                    {message.responses.map(response => (
                        <div key={response.id} className="response-card">
                            <div className="message-top">
                                <div className="message-text">
                                    <strong className="message-author">{response.author}</strong>
                                    <span>{response.message}</span>
                                </div>
                                <div className="message-right">
                                    <button
                                        className="message-action"
                                        type="button"
                                        title="Reply to response"
                                        aria-label={`Reply to ${response.author}`}
                                        onClick={() => onReply(message.months)}
                                    >
                                        <i className="fa-solid fa-reply" aria-hidden="true" />
                                    </button>
                                    {canDeleteMessage(message.author) &&
                                        <button
                                            className="message-action message-action-delete"
                                            type="button"
                                            title="Delete response"
                                            aria-label="Delete response"
                                            onClick={() => deleteResponse(response)}
                                        >
                                            <i className="fa-solid fa-trash" aria-hidden="true" />
                                        </button>
                                    }
                                </div>
                            </div>

                            <div className="meta">
                                <span className="sent"><i className="fa-regular fa-clock" aria-hidden="true" /> {toPrettyDate(response.createdAt)}</span>
                                {response.deadline && <span className="deadline"><i className="fa-regular fa-calendar-check" aria-hidden="true" /> Due {toPrettyDate(response.deadline)}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )
            }

        </div >
    );
}